import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import LazyImage from "../ui/LazyImage";
import type { OrganizationPageService } from "../../presentation/organizations/organizationPage.types";
import whatsappIcon from "../../assets/images/icon-whatsapp.png";

interface ServiceCardProps {
  service: OrganizationPageService;
  fallbackImage: string;
  actionHref?: string;
  wide?: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, fallbackImage, actionHref, wide = false }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const image = service.images[selectedImage] ?? service.images[0];
  const hasGallery = service.images.length > 1;
  const usesFlyerPreview = service.id.endsWith("-don-juan");
  const price = service.priceFrom ?? service.price;

  useEffect(() => {
    setSelectedImage(0);
  }, [service.id]);

  useEffect(() => {
    if (!isPreviewOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsPreviewOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = "";
    };
  }, [isPreviewOpen]);

  return (
    <Card data-service-id={service.id} className={`h-full overflow-hidden ${wide ? "lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]" : "flex flex-col"}`}>
      <div className={`min-w-0 ${usesFlyerPreview ? "bg-gradient-to-br from-emerald-50 to-amber-50" : "bg-gray-50"}`}>
        <button type="button" onClick={() => setIsPreviewOpen(true)} className="group relative block w-full text-left" aria-label={`Ver información completa de ${service.name}`}>
          <LazyImage
            src={image?.src ?? fallbackImage}
            alt={image?.alt ?? service.name}
            fallbackSrc={fallbackImage}
            width="100%"
            height={usesFlyerPreview ? "22rem" : hasGallery ? "18rem" : "14rem"}
            className={
              usesFlyerPreview
                ? "h-[22rem] w-full object-contain p-3 transition-transform duration-300 group-hover:scale-[1.02]"
                : hasGallery
                  ? `h-72 w-full bg-gray-50 object-contain ${wide ? "lg:h-[32rem]" : ""}`
                  : `h-56 w-full object-cover ${wide ? "lg:h-[32rem]" : ""}`
            }
            objectFit={usesFlyerPreview || hasGallery ? "contain" : "cover"}
          />
          <span className="absolute bottom-3 right-3 rounded-full bg-black/75 px-3 py-1.5 text-sm font-semibold text-white shadow transition group-hover:bg-black">Ver información completa</span>
        </button>
        {hasGallery && (
          <div className="flex gap-2 overflow-x-auto bg-white p-2" aria-label={`${service.name} gallery`}>
            {service.images.map((thumbnail, index) => (
              <button
                key={`${thumbnail.src}-${index}`}
                type="button"
                aria-label={`${service.name}: ${index + 1}`}
                aria-pressed={selectedImage === index}
                onClick={() => setSelectedImage(index)}
                className={`h-16 w-16 flex-none overflow-hidden rounded-md border-2 ${
                  selectedImage === index ? "border-primary" : "border-transparent"
                }`}
              >
                <LazyImage
                  src={thumbnail.src}
                  alt={thumbnail.alt}
                  fallbackSrc={fallbackImage}
                  width="4rem"
                  height="4rem"
                  className="h-full w-full object-cover"
                  objectFit="cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-col bg-white">
        <CardHeader>
          <CardTitle className={wide ? "text-3xl leading-tight text-gray-900 lg:text-4xl" : undefined}>
            {service.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col">
          {service.description && <ul className="flex-1 space-y-1.5 text-gray-600">{service.description.split("\n").map((item) => <li key={item} className="flex gap-2"><span aria-hidden="true" className="text-primary">•</span><span>{item}</span></li>)}</ul>}
          {price && <p className="mt-4 text-lg font-bold text-primary">{price}</p>}
          {actionHref && service.primaryAction?.label && (
            <a
              href={actionHref}
              target={service.primaryAction.method === "email" ? undefined : "_blank"}
              rel="noopener noreferrer"
              className={`mt-5 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 font-semibold text-white ${
                service.primaryAction.method === "whatsapp" ? "bg-whatsapp hover:bg-whatsapp-dark" : "bg-primary"
              }`}
            >
              {service.primaryAction.method === "whatsapp" && (
                <img src={whatsappIcon} alt="" aria-hidden="true" className="h-5 w-5" />
              )}
              {service.primaryAction.label}
            </a>
          )}
        </CardContent>
      </div>
      {isPreviewOpen && (
        <div role="dialog" aria-modal="true" aria-label={`Información completa de ${service.name}`} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) setIsPreviewOpen(false); }}>
          <div className="relative max-h-full max-w-5xl">
            <button type="button" onClick={() => setIsPreviewOpen(false)} autoFocus aria-label="Cerrar imagen" className="absolute -right-2 -top-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-2xl font-bold text-gray-900 shadow-lg hover:bg-gray-100">×</button>
            <img src={image?.src ?? fallbackImage} alt={image?.alt ?? service.name} className="max-h-[90vh] max-w-[92vw] rounded-xl object-contain shadow-2xl" />
          </div>
        </div>
      )}
    </Card>
  );
};

export default React.memo(ServiceCard);
